import { baseApi } from './baseApi';

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Register
    register: builder.mutation({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
    }),

    // Login
    login: builder.mutation({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),

    // Verify 2FA (after login when requires2FA=true) — supports totpCode or backupCode
    verify2FA: builder.mutation({
      query: (body) => ({ url: '/auth/verify-2fa', method: 'POST', body }),
    }),

    // Refresh token (uses httpOnly cookie automatically)
    refreshToken: builder.mutation({
      query: () => ({ url: '/auth/refresh', method: 'POST' }),
    }),

    // Logout
    logout: builder.mutation({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try { await queryFulfilled; } finally {
          dispatch(baseApi.util.resetApiState());
          localStorage.removeItem('accessToken');
          localStorage.removeItem('activeWorkspaceId');
        }
      },
    }),

    // Forgot password
    forgotPassword: builder.mutation({
      query: (body) => ({ url: '/auth/forgot-password', method: 'POST', body }),
    }),

    // Reset password
    resetPassword: builder.mutation({
      query: (body) => ({ url: '/auth/reset-password', method: 'POST', body }),
    }),

    // Verify email
    verifyEmail: builder.mutation({
      query: (body) => ({ url: '/auth/verify-email', method: 'POST', body }),
    }),

    // Request account recovery (lost 2FA device + no backup codes)
    requestRecovery: builder.mutation({
      query: (body) => ({ url: '/auth/request-recovery', method: 'POST', body }),
    }),

    // Complete account recovery (disables 2FA via email-verified token)
    completeRecovery: builder.mutation({
      query: (body) => ({ url: '/auth/complete-recovery', method: 'POST', body }),
    }),

    // Get current user profile
    getMe: builder.query({
      query: () => '/auth/me',
      providesTags: ['User'],
    }),

    // Update profile
    updateProfile: builder.mutation({
      query: (body) => ({ url: '/auth/me', method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),

    // Change password
    changePassword: builder.mutation({
      query: (body) => ({ url: '/auth/change-password', method: 'POST', body }),
    }),

    // Setup 2FA — returns { secret, otpauthUrl }
    setup2FA: builder.mutation({
      query: () => ({ url: '/auth/setup-2fa', method: 'POST' }),
      invalidatesTags: ['User'],
    }),

    // Confirm 2FA — verify first TOTP code, enables 2FA, returns backup codes
    confirm2FA: builder.mutation({
      query: (body) => ({ url: '/auth/confirm-2fa', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),

    // Disable 2FA
    disable2FA: builder.mutation({
      query: (body) => ({ url: '/auth/disable-2fa', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),

    // Get sessions
    getSessions: builder.query({
      query: () => '/auth/sessions',
    }),

    // Revoke session
    revokeSession: builder.mutation({
      query: (sessionId) => ({ url: `/auth/sessions/${sessionId}`, method: 'DELETE' }),
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useVerify2FAMutation,
  useRefreshTokenMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailMutation,
  useRequestRecoveryMutation,
  useCompleteRecoveryMutation,
  useGetMeQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useSetup2FAMutation,
  useConfirm2FAMutation,
  useDisable2FAMutation,
  useGetSessionsQuery,
  useRevokeSessionMutation,
} = authApi;
